import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: "What types of washers and dryers do you offer?",
    answer: "We offer a wide range of high-quality washers and dryers, including front-load washers, top-load washers, electric dryers, and washer-dryer combos from leading brands. Our selection includes both regular and featured models to suit different needs and budgets."
  },
  {
    question: "Do you provide installation services?",
    answer: "Yes, we provide professional installation services by certified technicians. Our installation service includes delivery, setup, and testing of your new appliance. We also offer old appliance removal if needed."
  },
  {
    question: "What areas do you service?",
    answer: "We service locations within a 30-mile radius of our service center in Santa Ana, CA. Before scheduling an installation, we'll verify if your location is within our service area."
  },
  {
    question: "What warranty coverage do you provide?",
    answer: "Our appliances come with a warranty that covers replacement if the unit breaks within the warranty period. After warranty expiration, you receive 50% off your next purchase (valid for 1 year after warranty ends)."
  },
  {
    question: "How do I schedule an installation?",
    answer: "You can schedule an installation through our website by selecting your desired product and clicking the 'Book Installation' button. Our team will verify your location and contact you to confirm the installation date and time."
  },
  {
    question: "What payment methods do you accept?",
    answer: "Payment is due upon successful installation. We accept all major credit cards, cash, and approved financing options."
  },
  {
    question: "What should I do to prepare for installation?",
    answer: "Ensure there's a clear path to the installation location, verify your space measurements, and make sure someone over 18 years old will be present during installation. Our team will handle the rest!"
  },
  {
    question: "Do you offer trade-in options?",
    answer: "Yes, we accept trade-ins for your old appliances. During the booking process, you can indicate if you have a trade-in and whether you need haul-away service."
  }
];

export function FAQ() {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems(current =>
      current.includes(index)
        ? current.filter(i => i !== index)
        : [...current, index]
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </Link>

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 sm:p-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h1>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => toggleItem(index)}
                      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">{faq.question}</span>
                      {openItems.includes(index) ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    
                    {openItems.includes(index) && (
                      <div className="px-4 pb-4">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <h2 className="text-lg font-semibold text-blue-900 mb-2">
                  Still have questions?
                </h2>
                <p className="text-blue-800">
                  Contact our support team at{' '}
                  <a href="tel:909‑990‑9090" className="text-blue-600 hover:text-blue-800">
                    909‑990‑9090
                  </a>
                  {' '}or{' '}
                  <a href="mailto:vholicllc@gmail.com" className="text-blue-600 hover:text-blue-800">
                    vholicllc@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}